// packages/web-ui/src/panels/files/FilesPanel.tsx
//
// Story W3-S5 — composite Files context-panel body. Consumes the file-tree
// state slice + the FileTree / FileContextMenu / InlineRename components.
//
// Responsibilities:
//  - Initial fetch on mount (and a `[Retry]` action on error)
//  - Search input with 150 ms debounce + chip-filter strip
//  - Three states: loading skeleton / empty / error / populated
//  - Right-click context-menu → onAction routing to the consumer
//  - Inline rename UX → routes through `useFilesStore.renamePath`
//
// This file is independent of the AppShell `<FilesMode>` chrome (W2-S3 owns
// that file). Wiring `<FilesMode>` to mount this panel is a follow-up done
// by the AppShell agent — that change is OUT OF SCOPE for W3-S5 to avoid
// stepping on the W2 lane.

import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FileTree,
  FileContextMenu,
  InlineRename,
  filterTree,
  flattenTree,
  isFilterActive,
  type ContextMenuAction,
} from '../../components/file-tree/index.js';
import {
  CHIP_FILTERS,
  PULSE_DURATION_MS,
  useFilesStore,
  type ChipFilter,
} from '../../state/files.js';
import type { FileTreeNode } from '../../api/clients/files.js';

const DEBOUNCE_MS = 150;

export interface FilesPanelProps {
  /** Whether the syside-IDE capability is present (drives menu enabled state). */
  sysideAvailable?: boolean;
  /** Open-resource callback — typically `useOpenResource()('files', path)`. */
  onOpen?: (path: string) => void;
  /** Open-in-syside callback. */
  onOpenInSyside?: (path: string) => void;
  /** Reveal-in-Finder callback (delegates to systemApi). */
  onRevealInFinder?: (path: string) => void;
  /** Copy-path callback (defaults to `navigator.clipboard.writeText`). */
  onCopyPath?: (path: string) => void;
  /** Per-row flags accessor. */
  getRowFlags?: Parameters<typeof FileTree>[0]['getRowFlags'];
  /** Test seam — bypass real timers when set to `false`. */
  enableDebounce?: boolean;
}

interface MenuState {
  node: FileTreeNode;
  x: number;
  y: number;
}

interface RenameState {
  path: string;
  parentPath?: string;
}

export function FilesPanel(props: FilesPanelProps): JSX.Element {
  const {
    sysideAvailable = true,
    onOpen,
    onOpenInSyside,
    onRevealInFinder,
    onCopyPath,
    getRowFlags,
    enableDebounce = true,
  } = props;

  const tree = useFilesStore((s) => s.tree);
  const loading = useFilesStore((s) => s.loading);
  const lastError = useFilesStore((s) => s.lastError);
  const expansionState = useFilesStore((s) => s.expansionState);
  const pulsedPaths = useFilesStore((s) => s.pulsedPaths);
  const pendingRenames = useFilesStore((s) => s.pendingRenames);
  const chipFilter = useFilesStore((s) => s.chipFilter);
  const query = useFilesStore((s) => s.query);
  const selectedPath = useFilesStore((s) => s.selectedPath);
  const loadTree = useFilesStore((s) => s.loadTree);
  const setExpanded = useFilesStore((s) => s.toggleExpanded);
  const setChipFilter = useFilesStore((s) => s.setChipFilter);
  const setQuery = useFilesStore((s) => s.setQuery);
  const setSelectedPath = useFilesStore((s) => s.setSelectedPath);
  const renamePath = useFilesStore((s) => s.renamePath);

  const [searchInput, setSearchInput] = useState(query);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [renameState, setRenameState] = useState<RenameState | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial fetch on mount
  useEffect(() => {
    void loadTree({});
  }, [loadTree]);

  // Debounced search → store
  useEffect(() => {
    if (!enableDebounce) {
      setQuery(searchInput);
      return;
    }
    if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setQuery(searchInput);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
    };
  }, [searchInput, setQuery, enableDebounce]);

  // Filtered + flattened rows. When a filter is active we force every dir
  // open so the matches surface; otherwise we honour the user's expansion.
  const rows = useMemo(() => {
    const active = isFilterActive(chipFilter, query);
    const filtered = filterTree(tree, chipFilter, query);
    if (active) {
      return flattenTree(filtered, new Map(), { defaultExpansion: 'all-expanded' });
    }
    return flattenTree(filtered, expansionState);
  }, [tree, chipFilter, query, expansionState]);

  const filteredRowsForFlags = useCallback(
    (node: FileTreeNode) => {
      const baseFlags = getRowFlags?.(node) ?? {};
      return {
        ...baseFlags,
        hasErrors: baseFlags.hasErrors ?? node.hasErrors === true,
        isPulsing: pulsedPaths.has(node.path) || baseFlags.isPulsing === true,
        isRenamePending: pendingRenames.has(node.path) || baseFlags.isRenamePending === true,
      };
    },
    [getRowFlags, pulsedPaths, pendingRenames],
  );

  const handleAction = useCallback(
    async (action: ContextMenuAction, node: FileTreeNode) => {
      switch (action) {
        case 'open':
          if (node.kind === 'file') onOpen?.(node.path);
          else setExpanded(node.path);
          return;
        case 'openInSyside':
          onOpenInSyside?.(node.path);
          return;
        case 'revealInFinder':
          onRevealInFinder?.(node.path);
          return;
        case 'copyPath':
          if (typeof onCopyPath === 'function') {
            onCopyPath(node.path);
          } else if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
            try {
              await navigator.clipboard.writeText(node.path);
            } catch {
              // Browser denied clipboard access — caller's toast layer handles it.
            }
          }
          return;
        case 'rename':
          if (node.kind === 'file') {
            setRenameState({ path: node.path, parentPath: pathParent(node.path) });
          }
          return;
        default:
          return;
      }
    },
    [onOpen, onOpenInSyside, onRevealInFinder, onCopyPath, setExpanded],
  );

  const handleRenameCommit = useCallback(
    async (newName: string): Promise<{ ok: true } | { ok: false; message: string }> => {
      if (renameState === null) return { ok: false, message: 'no rename in flight' };
      const newPath =
        renameState.parentPath !== undefined
          ? `${renameState.parentPath}/${newName}`
          : newName;
      const res = await renamePath({ path: renameState.path, newPath });
      if (res.ok) {
        setRenameState(null);
        return { ok: true };
      }
      return { ok: false, message: res.error.message };
    },
    [renameState, renamePath],
  );

  // Three-state body
  let body: JSX.Element;
  if (loading) {
    body = (
      <ul data-testid="files-panel-skeleton" className="flex flex-col gap-1 p-3">
        <li className="h-4 animate-pulse rounded bg-cream-200" />
        <li className="h-4 animate-pulse rounded bg-cream-200" />
        <li className="h-4 animate-pulse rounded bg-cream-200" />
      </ul>
    );
  } else if (lastError !== null) {
    body = (
      <div data-testid="files-panel-error" className="p-3 text-xs">
        <p>Could not load file tree: {lastError.message}</p>
        <button
          type="button"
          data-testid="files-panel-retry"
          onClick={() => {
            void loadTree({});
          }}
          className="mt-2 rounded border border-taupe-500 bg-cream-200 px-3 py-1"
        >
          Retry
        </button>
      </div>
    );
  } else if (tree.length === 0) {
    body = (
      <div data-testid="files-panel-empty" className="p-3 text-xs">
        No .sysml / .json / .md files found in this workspace.
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <div data-testid="files-panel-no-matches" className="p-3 text-xs">
        <p>No matches for &quot;{query}&quot;.</p>
        <button
          type="button"
          data-testid="files-panel-clear-filter"
          onClick={() => {
            setSearchInput('');
            setQuery('');
            setChipFilter('All');
          }}
          className="mt-2 rounded border border-taupe-500 bg-cream-200 px-3 py-1"
        >
          Clear filter
        </button>
      </div>
    );
  } else {
    body = (
      <FileTree
        rows={rows}
        selectedPath={selectedPath}
        onSelectionChange={setSelectedPath}
        onOpen={(path) => onOpen?.(path)}
        onToggleExpand={setExpanded}
        onContextMenu={(node, x, y) => setMenu({ node, x, y })}
        getRowFlags={filteredRowsForFlags}
        query={query}
      />
    );
  }

  return (
    <div data-testid="files-panel" className="flex h-full flex-col">
      <div data-testid="files-panel-header" className="border-b border-taupe-300 px-2 py-1">
        <input
          type="search"
          data-testid="files-panel-search"
          placeholder="Filter files"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="h-6 w-full rounded border border-taupe-300 bg-cream-50 px-2 text-xs"
        />
        <div data-testid="files-panel-chips" className="mt-1 flex gap-1">
          {CHIP_FILTERS.map((chip: ChipFilter) => (
            <button
              key={chip}
              type="button"
              data-testid={`files-panel-chip-${chip.replace('.', '')}`}
              data-active={chipFilter === chip ? 'true' : 'false'}
              onClick={() => setChipFilter(chip)}
              className={
                'rounded border border-taupe-300 px-2 py-0.5 text-[10px] '
                + (chipFilter === chip ? 'bg-cream-200' : 'bg-cream-50')
              }
            >
              {chip}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto" data-testid="files-panel-body">{body}</div>

      {renameState !== null ? (
        <div
          data-testid="files-panel-rename-host"
          className="border-t border-taupe-300 p-2"
        >
          <div className="mb-1 text-[10px] text-taupe-700">
            Rename {renameState.path}
          </div>
          <InlineRename
            initialName={basename(renameState.path)}
            onCommit={handleRenameCommit}
            onCancel={() => setRenameState(null)}
          />
        </div>
      ) : null}

      <FileContextMenu
        node={menu?.node ?? null}
        x={menu?.x ?? 0}
        y={menu?.y ?? 0}
        sysideAvailable={sysideAvailable}
        onAction={handleAction}
        onClose={() => setMenu(null)}
      />
    </div>
  );
}

// ---- Internal pure helpers (exported for tests) -------------------------

export function basename(p: string): string {
  const parts = p.split('/');
  return parts[parts.length - 1] ?? p;
}

export function pathParent(p: string): string | undefined {
  const idx = p.lastIndexOf('/');
  return idx === -1 ? undefined : p.slice(0, idx);
}

// Re-exported pulse duration for consumers wiring the prune timer.
export { PULSE_DURATION_MS };
